import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { z } from 'zod';
import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import {
  ValidUuidOrNotSetSchema,
  withoutObjectKeysWhereValueIs,
} from '@vestigas/utils';
import { i18n } from '~/utils';

import { queryKeysFtpPoller } from './queryKeys';

export const FTP_PROTOCOL = ['ftp', 'sftp'] as const;

export const FTP_FILE_TYPE = ['generic', 'pdf', 'pdf_ai', 'ugl'] as const;

export const zodFtpPollerPayloadSchema = z.object({
  batchSize: z.number().int().min(1).optional(),
  buyerId: ValidUuidOrNotSetSchema,
  confirmLargeBatch: z.boolean().optional(),
  contextData: z.object({ buyerNr: z.string().nullish() }).optional(),
  deleteSource: z
    .enum(['true', 'false'])
    .optional()
    .prefault('true')
    .transform((value) => value === 'true'),
  extremeBatchThreshold: z.number().int().min(1).optional(),
  fileType: z.enum(FTP_FILE_TYPE).prefault('generic'),
  isActive: z
    .enum(['true', 'false'])
    .optional()
    .prefault('true')
    .transform((value) => value === 'true'),
  issuerId: z.uuid(),
  largeBatchThreshold: z.number().int().min(1).optional(),
  maxFileAgeDays: z.number().int().min(1).nullable().optional(),
  password: z.string().min(1),
  path: z.string().min(1),
  port: z.coerce.number().min(1).max(65_535),
  protocol: z.enum(FTP_PROTOCOL).prefault('sftp'),
  server: z.string(), // FTP server address; z.string().url()?
  username: z.string().min(1),
});

export const zodCreateFtpPollerPayloadSchema = zodFtpPollerPayloadSchema
  .transform((value) => withoutObjectKeysWhereValueIs(value, [null, undefined]))
  .transform(({ buyerId, ...rest }) => ({
    buyerId: buyerId || null, // Replace empty string with null
    ...rest,
  }));

export type CreateFtpPollerPayload = z.infer<
  typeof zodCreateFtpPollerPayloadSchema
>;

type CreateFtpPollerResponse = {
  code: number;
  message: string;
  requestId: UUID | undefined;
};

/**
 * Create a new FTP poller secret.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-FTP-Secrets/operation/create_ftp_secret_vadmin_ftp_poller_secret_post
 */
export const createFtpPoller = async (
  createFtpPollerPayload: Partial<CreateFtpPollerPayload>,
) => {
  try {
    const response = await vestigasApi
      .post(ENDPOINT.FTP_POLLER.CREATE(), {
        json: createFtpPollerPayload,
        validate: { body: zodCreateFtpPollerPayloadSchema },
      })
      .json<CreateFtpPollerResponse>();

    return response;
  } catch (error) {
    // 400 (connection test errors), 412 (large batch), 403 (extreme batch)
    // are handled by the form layer — do not show a toast here for those.
    if (
      error instanceof HTTPError &&
      [400, 403, 409, 412].includes(error.response.status)
    ) {
      throw error;
    }

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 422: {
          errorMessage = i18n.t(($) => $.mutation.createFtpPoller.error422, {
            ns: 'ftpPoller',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) => $.mutation.createFtpPoller.error.generic,
            {
              ns: 'ftpPoller',
            },
          );
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * A custom hook to create a new FTP poller secret.
 * Uses the `useMutation` hook from `@tanstack/react-query` internally.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-FTP-Secrets/operation/create_ftp_secret_vadmin_ftp_poller_secret_post
 */
export const useMutationCreateFtpPoller = (
  options?: UseMutationOptions<
    CreateFtpPollerResponse,
    unknown,
    Partial<CreateFtpPollerPayload>
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    CreateFtpPollerResponse,
    unknown,
    Partial<CreateFtpPollerPayload>
  >({
    mutationFn: async (ftpPollerPayload) => createFtpPoller(ftpPollerPayload),
    onError(_error) {
      return; // prevent error from bubbling up further unless overridden via mutation options
    },
    onSettled() {
      void queryClient.invalidateQueries({
        queryKey: queryKeysFtpPoller.getAll(undefined),
      });
    },
    onSuccess() {
      ToastService.info(
        i18n.t(($) => $.mutation.createFtpPoller.success, { ns: 'ftpPoller' }),
      );
    },
    ...options,
  });
};
